import React, { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface AdminTagChipPickerProps {
  tags: CorkBoardTag[];
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;
  maxTags?: number;
  label?: string;
  error?: string;
}

export const AdminTagChipPicker: React.FC<AdminTagChipPickerProps> = memo(
  ({
    tags,
    selectedTagIds,
    onToggleTag,
    maxTags = 1,
    label,
    error,
  }) => {
    return (
      <View style={styles.container}>
        {label ? <Text style={styles.label}>{label}</Text> : null}

        <View style={styles.tagGrid}>
          {tags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            return (
              <TouchableOpacity
                key={tag.id}
                style={[
                  styles.tagChip,
                  isSelected && styles.tagChipSelected,
                  isSelected && tag.color ? { borderColor: tag.color } : null,
                ]}
                onPress={() => onToggleTag(tag.id)}
              >
                {tag.icon ? <Text style={styles.tagIcon}>{tag.icon}</Text> : null}
                <Text
                  style={[
                    styles.tagName,
                    isSelected && styles.tagNameSelected,
                    isSelected && tag.color ? { color: tag.color } : null,
                  ]}
                >
                  {tag.name}
                </Text>
                {isSelected && <SVG_COMPONENTS.CHECK_CIRCLE width={12} height={12} color={ADMIN_COLORS.PRIMARY} />}
              </TouchableOpacity>
            );
          })}
        </View>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        <Text style={styles.hint}>
          {selectedTagIds.length} of {maxTags} tags selected
        </Text>
      </View>
    );
  },
);

AdminTagChipPicker.displayName = 'AdminTagChipPicker';

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  tagGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  tagChipSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
    backgroundColor: 'rgba(107, 142, 254, 0.1)',
  },
  tagChipDisabled: {
    opacity: 0.4,
  },
  tagIcon: {
    fontSize: 14,
  },
  tagName: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  tagNameSelected: {
    color: ADMIN_COLORS.PRIMARY,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
  },
  hint: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
});
